import clsx from 'clsx'
import { useAtom } from 'jotai'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useAttachments } from 'api/queries/useAttachments'
import { useFetchDetails } from 'api/queries/useFetchDetails'
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg'
import { ModalAtom } from 'atoms/modals.atom'
import { WinnerTag } from 'components/awardCard/WinnerTag'
import { FullScreenModal } from 'components/uikit/FullScreenModal'
import { Icon } from 'components/uikit/Icon'
import { Attachment, AwardsAttachments } from 'types/AwardsAttachments'
import { AwardsFile } from 'types/AwardsFile'
import { getBySlug } from 'utils/slugs'

import modalStyles from './awardModal.module.scss'
import { DetailsComponent } from './details/details'
import { InfoComponent } from './info/info'
import { Gallery } from './mediaSlider/Gallery'
import { SideMenuComponent } from './sideMenu/sideMenu'
import VideoPreview from './videoPreview/VideoPreview'

declare global {
  interface Window {
    Intercom: (command: string, props?: any) => void
  }
}

export const AwardModal = () => {
  const [modal, setModal] = useAtom(ModalAtom)
  const { data: submissionInfo } = useFetchDetails(modal.award?.slug ? modal.award.slug : '', {
    enabled: modal.isOpen,
  }) as {
    data: AwardsFile
  }
  const { data: Attachments, isLoading: isAttachmentLoading } = useAttachments(
    modal.award?.slug ? modal.award.slug : '',
    {
      enabled: modal.isOpen,
    },
  ) as {
    data: AwardsAttachments
    isLoading: boolean
  }
  const [savedAttachments, setSavedAttachments] = useState<Attachment[]>([])
  const [, setShow] = useState(false)
  const [showVideo, setShowVideo] = useState(false)
  const [selectedSection, setSelectedSection] = useState('strategy')

  const [activeVideo, setActiveVideo] = useState<string | undefined>(undefined)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [imgSrc, setImgSrc] = useState<string | undefined>(undefined)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (videoRef.current && !videoRef.current.contains(event.target as Node)) {
        setActiveVideo(undefined)
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActiveVideo(undefined)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  useEffect(() => {
    if (modal.isOpen) {
      setImgSrc(modal.award?.thumbnail)
      window.Intercom('update', { hide_default_launcher: true })
    }
  }, [modal.isOpen, modal.award?.thumbnail])

  const fetchNewAttachments = useCallback(async () => {
    setShow(true)
  }, [])

  useEffect(() => {
    const updateAttachments = async () => {
      if (Attachments.attachments && JSON.stringify(Attachments.attachments) !== JSON.stringify(savedAttachments)) {
        setSavedAttachments(Attachments.attachments as Attachment[])
        await fetchNewAttachments()
      }
    }

    updateAttachments()
  }, [Attachments.attachments, savedAttachments, fetchNewAttachments])

  if (!modal.isOpen || !modal.award) {
    return null
  }

  const handleClose = () => {
    setShow(false)
    setSelectedSection('strategy')
    setModal({ isOpen: false, award: modal.award })
    setActiveVideo(undefined)
    setShowVideo(false)
    window.Intercom('update', { hide_default_launcher: false })
  }

  const sectionChange = (section: string) => {
    setSelectedSection(section)
  }

  const sectionsInfo = (slug: string) => {
    return getBySlug(submissionInfo.data?.fields, slug)
  }

  return (
    <FullScreenModal className={modalStyles.modal} disableOutsideClick open={modal.isOpen}>
      <div slot="header" className="flex flex-1 justify-between w-full items-start">
        <h2 className={clsx('pl-16 text-[24px] font-TCCC font-[600]')}>{modal.award.name}</h2>
        <Icon
          icon={CloseIcon}
          className="flex items-center cursor-pointer hover:opacity-80"
          color="black"
          onClick={handleClose}
        />
      </div>
      <div slot="body">
        <div className="flex justify-between gap-x-[52px]">
          <div className="flex flex-1 flex-col w-full border-black">
            <div className={clsx('w-full mx-auto mb-6 h-[415px] relative')}>
              {submissionInfo.winner ? (
                <div className="absolute z-30 top-2 left-2">
                  <WinnerTag />
                </div>
              ) : (
                ''
              )}
              <Gallery srcSet={Attachments.attachments} placeholder={imgSrc} loading={isAttachmentLoading} />
              {showVideo && <VideoPreview src={activeVideo} />}
            </div>
            {modal.award.shortlist ? (
              <div className={`${modalStyles.shortlist} mb-3 px-1.5 bg-[#FD900C]`}>Shortlist</div>
            ) : (
              ''
            )}
            <div className="flex pb-3 w-full mx-auto">
              <div className="mr-6">
                <SideMenuComponent selected={selectedSection} onClick={sectionChange} />
              </div>
              {selectedSection !== 'details' ? (
                submissionInfo?.data && <InfoComponent sections={sectionsInfo(selectedSection)} />
              ) : (
                <DetailsComponent info={submissionInfo} attachments={Attachments} loading={isAttachmentLoading} />
              )}
            </div>
          </div>
        </div>
      </div>
    </FullScreenModal>
  )
}
